import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Directive, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, debounceTime, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FormType } from 'src/app/shared/interfaces/form-type';
import { City, Street } from 'src/app/api/v1/models';
import { CitiesService } from 'src/app/services/cities.service';
import { FormStepAbstract } from '../../../../../shared/components/abstract/form-step.abstract';
import { AlphanumericValidator, FlatValidator } from '../../../../../shared/validators/text.validators';
import { FormSaveStrategy } from '../../../strategy/form-save-strategy/form-save-strategy.abstract';
import { AddressSelectorService } from './address-selector.service';

interface IFullAddressControl {
  city: string | null,
  street: string | null,
  house: string | null,
  flat: string | null,
  building: string | null,
  porch: string | null,
  floor: string | null,
  streetClassifierId: string | null,
}

@UntilDestroy()
@Directive()
export class FullAddressStepAbstract extends FormStepAbstract implements OnInit {
  private cities: CitiesService = inject(CitiesService);
  private addressSelectorService: AddressSelectorService = inject(AddressSelectorService);
  private formSaveStrategy: FormSaveStrategy = inject(FormSaveStrategy);

  @Input() isFullView: boolean = true;

  @ViewChild(CdkVirtualScrollViewport, {static: false}) cdkVirtualScrollViewPort: CdkVirtualScrollViewport;

  override form: FormGroup<FormType<IFullAddressControl>> = new FormGroup<FormType<IFullAddressControl>>({
    city: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    street: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
    house: new FormControl('', {nonNullable: true, validators: [Validators.maxLength(10), Validators.required]}),
    flat: new FormControl('', {validators: [Validators.maxLength(15), FlatValidator], nonNullable: true}),
    building: new FormControl('', {validators: [Validators.maxLength(10), AlphanumericValidator], nonNullable: true}),
    porch: new FormControl('', {validators: [Validators.maxLength(10), AlphanumericValidator], nonNullable: true}),
    floor: new FormControl('', {validators: [Validators.maxLength(10), AlphanumericValidator], nonNullable: true}),
    streetClassifierId: new FormControl(null),
  });

  selectCity: City;
  selectStreetName: string | undefined;

  private isInitialChangeCity: boolean = true;

  isLoadingCities$: Observable<boolean> = this.cities.isLoading$;
  cities$: Observable<City[] | null> = this.cities.cities$.pipe(
    tap((cities: City[] | null): void => {
      const cityValue = this.form.controls.city.value;
      if(!cities) {
        return;
      }

      this.subscribeToDeliveryAddress();

      if(!cityValue) {
        return;
      }
      if(!this.checkCityExist(cities, cityValue)) {
        this.form.controls.city.setValue(null);
        this.form.controls.street.setValue(null);
      }
    })
  );

  private deliverySub: Subscription;

  override ngOnInit(): void {
    super.ngOnInit();

    this.form.controls.street.disable();

    if(this.form.controls.street.value || this.form.controls.city.value) {
      this.form.controls.street.enable();
    }

    this.subscribeToDeliveryZone();
  }

  private addressSub: Subscription;

  private subscribeToDeliveryAddress(): void {
    this.addressSub?.unsubscribe();

    this.addressSub = this.addressSelectorService.deliveryAddress$.pipe(
      tap(deliveryAddress => {
        if(!deliveryAddress) {
          return;
        }

        this.selectStreetName = deliveryAddress.street || '';

        this.form.controls.city.setValue(deliveryAddress.city || '', {emitEvent: false});
        this.form.controls.street.setValue(deliveryAddress.street || '', {emitEvent: false});
        this.form.controls.house.setValue(deliveryAddress.house || '', {emitEvent: false});

        this.form.controls.porch.setValue(deliveryAddress.porch || '', {emitEvent: false});
        this.form.controls.floor.setValue(deliveryAddress.floor || '', {emitEvent: false});
        this.form.controls.building.setValue(deliveryAddress.building || '', {emitEvent: false});
        this.form.controls.flat.setValue(deliveryAddress.flat || '', {emitEvent: false});

        this.form.controls.streetClassifierId.setValue(deliveryAddress.streetClassifierId || '', {emitEvent: false});
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  private subscribeToDeliveryZone(): void {
    this.deliverySub = combineLatest([
      this.form.controls.city.valueChanges || of(null),
      this.form.controls.street.valueChanges || of(null),
      this.form.controls.house.valueChanges || of(null),
      this.form.controls.porch.valueChanges || of(null),
      this.form.controls.floor.valueChanges || of(null),
      this.form.controls.building.valueChanges || of(null),
      this.form.controls.flat.valueChanges || of(null),
      this.form.controls.streetClassifierId.valueChanges || of(null),
    ]).pipe(
      debounceTime(this.formSaveStrategy.debounceTimeout),
      map(() => this.form.getRawValue()),
      filter(formValue => !!formValue && !!formValue.city && !!formValue.street && !!formValue.house),
      switchMap((formValue) => this.formSaveStrategy.setDeliveryAddress(formValue)),
      tap((formValue) => {
        this.cities.setDeliveryAddress(formValue);
      }),
      untilDestroyed(this),
    ).subscribe();
  }

  onChangeCityOption($event: MatOptionSelectionChange, city: City): void {
    if(this.isInitialChangeCity) {
      this.isInitialChangeCity = false;
      this.selectCity = city;
      this.form.controls.street.enable();
    }

    if(!$event.isUserInput) {
      return;
    }
    this.selectCity = city;
    this.form.controls.street.setValue(null);
    this.form.controls.street.enable();
  }

  private checkCityExist(cities: City[], name: string): boolean {
    let result = false;

    if(!cities || !name) {
      return false;
    }

    for(let i = 0; i < cities.length; i++) {
      if(cities[i].name == name) {
        result = true;
        break;
      }
    }

    return result;
  }

  onChangeStreet($event: MatOptionSelectionChange, street: Street): void {
    if(!$event.isUserInput) {
      return;
    }
    this.selectStreetName = street.name;
    this.form.controls.streetClassifierId.setValue(street.classifierId || null);
  }
}
